import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Banner from "../components/Banner/banner";
import Insights from "../components/Insights/Insights";
import ReportTable from "../components/ReportTable/ReportTable";
import { getReportData } from "../../../services/reportService";
import { IReportData } from "../interfaces/IReport";
import Loader from "../../../components/ui/Loader";
import styles from "./report.module.css";
import { useUser } from "../../../context/UserContext";
import HrCompletionComment from "../components/HrCompletionComment/HrCompletionComment";
import HrCommentDisplay from "../components/HrCompletionComment/HrCommentDisplay";
import { startManagerEvaluation } from "../../../services/evaluationsService";

export default function Report() {
  const { user } = useUser();
  const { evaluationid } = useParams<{ evaluationid: string }>();
  const navigate = useNavigate();
  const [reportData, setReportData] = useState<IReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isStartingEvaluation, setIsStartingEvaluation] = useState(false);
  const [finalNote, setFinalNote] = useState<string | null>(null);
  const [, setError] = useState<Error | null>(null);

  const loadReport = useCallback(async () => {
    if (!evaluationid) {
      setError(new Error("ID da avaliação ausente"));
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await getReportData(evaluationid);
      setReportData(data);
    } catch (err: any) {
      console.error("Erro ao carregar relatório:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [evaluationid]);

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  useEffect(() => {
    const existingFinalEvaluation = reportData?.finalEvaluation?.trim();
    if (existingFinalEvaluation) {
      setFinalNote(existingFinalEvaluation);
      return;
    }

    setFinalNote(null);
  }, [reportData?.finalEvaluation]);

  if (loading) return <Loader text="A carregar relatório..." />;
  if (!reportData)
    return (
      <div className={styles.errorContainer}>
        <p className={styles.errorText}>Nenhum relatório disponível</p>
      </div>
    );

  // Check if the logged-in user is viewing their own report
  const isOwnReport =
    user?.name === reportData.evaluatorsByType.selfEvaluation?.name;
  const evaluatorHasSubmittedAnswers = (evaluatorId: string) =>
    reportData.categories.some((category) =>
      category.questions.some((question) =>
        question.answers.some(
          (answer) =>
            answer.evaluatorId === evaluatorId && answer.qualification !== "-"
        )
      )
    );
  const canManagerEvaluate =
    user?.typeLabel === "Management" &&
    !reportData.hasReport &&
    !reportData.evaluatorsByType.managers.some(
      (manager) =>
        manager.statusCode === 122470001 ||
        evaluatorHasSubmittedAnswers(manager.id)
    );
  const teamLeaderToEvaluate = reportData.evaluatorsByType.teamLeaders.find(
    (teamLeader) =>
      teamLeader.statusCode !== 122470001 &&
      !evaluatorHasSubmittedAnswers(teamLeader.id)
  );
  const canTeamLeaderEvaluate =
    user?.typeLabel === "Team Leader" &&
    !reportData.hasReport &&
    Boolean(teamLeaderToEvaluate);
  const endDate = new Date(reportData.evaluationEndDate);
  const millisecondsRemaining = endDate.getTime() - Date.now();
  const remainingDays = Math.ceil(millisecondsRemaining / (1000 * 60 * 60 * 24));
  const shouldShowRemainingTime =
    !reportData.hasReport &&
    reportData.evaluationStatus !== 122470005 &&
    !Number.isNaN(endDate.getTime()) &&
    millisecondsRemaining >= 0;

  const handleEvaluation = async () => {
    if (!evaluationid) return;

    setIsStartingEvaluation(true);
    try {
      if (canManagerEvaluate) {
        const { evaluatorId } = await startManagerEvaluation(evaluationid);
        navigate(`/questionnaire?id=${evaluatorId}`);
        return;
      }

      if (teamLeaderToEvaluate) {
        navigate(`/questionnaire?id=${teamLeaderToEvaluate.id}`);
      }
    } catch (error) {
      console.error("Erro ao iniciar avaliação:", error);
      alert("Não foi possível iniciar a avaliação.");
    } finally {
      setIsStartingEvaluation(false);
    }
  };

  return (
    <>
      <Banner
        bannerEmployeeName={
          reportData.evaluatorsByType.selfEvaluation?.name ?? "-"
        }
        bannerYear={"Relatório " + reportData.evaluationYear}
        bannerTitle={`Relatório ${reportData.evaluationYear}`}
        bannerDate={new Date(reportData.evaluationEndDate).toLocaleDateString(
          "pt-PT"
        )}
        showBreadcrumb={!isOwnReport}
        evaluationId={evaluationid}
        hasReport={reportData.hasReport}
        showEvaluate={canManagerEvaluate || canTeamLeaderEvaluate}
        onEvaluate={handleEvaluation}
        isEvaluating={isStartingEvaluation}
        remainingDays={shouldShowRemainingTime ? remainingDays : undefined}
        finalNote={finalNote ?? undefined}
      />
      <div style={{ background: "white" }}>
        <div className={styles.container}>
          <Insights
            evaluators={reportData.evaluatorsByType}
            employeeName="Colaborador"
          />

          <ReportTable
            categories={reportData.categories}
            evaluatorsByType={reportData.evaluatorsByType}
          />

          {reportData.canHrComplete && evaluationid && (
            <HrCompletionComment
              evaluationId={evaluationid}
              onCompleted={loadReport}
            />
          )}
          {!reportData.canHrComplete && reportData.hrComment && (
            <HrCommentDisplay comment={reportData.hrComment} />
          )}
        </div>
      </div>
    </>
  );
}
